import React from 'react';

import type { FieldType } from '@/shared/types';
import { parseStoredRichTextValue } from '@/shared/utils/tiptap';

import { ComplaintCellRenderer } from './complaint-cell.component';
import { LabeledEntriesCellRenderer } from './labeled-entries-cell.component';

const LIST_FIELD_TYPES = new Set<FieldType>(['drugOrder', 'diagnosis', 'radiology', 'investigation', 'immunization']);

const LABELED_ENTRY_FIELD_TYPES = new Set<FieldType>([
  'hopi',
  'systemicExaminations',
  'psychologyNote',
  'customSection',
  'consultationDetails',
]);

const parseNumberedLines = (value: string): string[] =>
  value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => (/^\d+\.\s/.test(line) ? line.replace(/^\d+\.\s*/, '') : line));

interface TimelineVisitCellProps {
  value: string;
  fieldType: FieldType;
}

export const TimelineVisitCell: React.FC<TimelineVisitCellProps> = ({ value, fieldType }) => {
  if (value === '-') {
    return <span className="text-sm italic text-slate-400">No data recorded</span>;
  }

  if (fieldType === 'complaint') {
    return (
      <div className="rounded-lg border border-slate-200/80 bg-slate-50/50 p-3">
        <ComplaintCellRenderer value={value} />
      </div>
    );
  }

  if (LABELED_ENTRY_FIELD_TYPES.has(fieldType)) {
    return <LabeledEntriesCellRenderer value={value} fieldLabel={fieldType} />;
  }

  if (LIST_FIELD_TYPES.has(fieldType) || /^\d+\.\s/m.test(value)) {
    const items = parseNumberedLines(value);

    return (
      <ol className="list-decimal space-y-1 pl-5 text-sm leading-8 text-slate-800 marker:font-semibold marker:text-primary/70">
        {items.map((item, index) => (
          <li key={`${index}-${item.slice(0, 32)}`} className="pl-1">
            {item}
          </li>
        ))}
      </ol>
    );
  }

  const parsed = parseStoredRichTextValue(value);

  if (parsed.isTipTapDoc && parsed.html) {
    return (
      <div
        className="minimal-tiptap-editor whitespace-pre-line text-sm leading-8 text-slate-800"
        dangerouslySetInnerHTML={{ __html: parsed.html }}
      />
    );
  }

  return <p className="whitespace-pre-line text-sm leading-8 text-slate-800">{value}</p>;
};

interface TimelineFieldLabelProps {
  fieldName: string;
}

export const TimelineFieldLabel: React.FC<TimelineFieldLabelProps> = ({ fieldName }) => (
  <span className="text-sm font-semibold tracking-wide text-primary-dark">{fieldName}</span>
);
